import { makeStyles } from '@material-ui/core'
import FormControl from '@material-ui/core/FormControl'
import FormHelperText from '@material-ui/core/FormHelperText'
import InputLabel from '@material-ui/core/InputLabel'
import MenuItem from '@material-ui/core/MenuItem'
import Typography from '@material-ui/core/Typography'
import { useFormikContext } from 'formik'
import { isEmpty } from 'lodash'
import React from 'react'

import { fetchEmandateStatus } from 'api'
import { Select } from 'app/components/generic/FormElements'
import { EMANDATE_METHODS } from 'app/pages/Class/Inventory/constants'
import { validateNotEmpty } from 'app/validators'

// ₹5,000 to ₹50,000 in ₹2,500 steps. This is the ceiling Razorpay will allow a future
// auto-debit to collect — not a charge. Nothing is debited when the mandate is set up.
export const EMANDATE_MAX_AMOUNT_OPTIONS = Array.from(
  { length: 19 },
  (_, i) => 5000 + i * 2500
)

const useStyles = makeStyles(() => ({
  note: {
    marginTop: 12,
    padding: '10px 12px',
    background: '#f6f6f6',
    borderRadius: 6,
    fontSize: 13,
    lineHeight: '20px'
  },
  warn: {
    marginTop: 12,
    padding: '10px 12px',
    background: '#fff6f3',
    border: '1px solid #EF5C34',
    borderRadius: 6,
    fontSize: 13,
    lineHeight: '20px'
  }
}))

const formatRupees = (n) => `₹${Number(n).toLocaleString('en-IN')}`

const SetupEmandateForm = (props) => {
  const classes = useStyles()
  const { formData } = props
  const { values, errors } = useFormikContext()
  const [existing, setExisting] = React.useState(null)

  // MasterForm maps the selected parent's USER uuid onto formData.uuid (see its data effect),
  // which is the same id the cart and the mandate-status endpoint key on.
  const userUuid = formData?.uuid

  React.useEffect(() => {
    let cancelled = false
    ;(async () => {
      if (!userUuid) return
      const data = await fetchEmandateStatus({ userUuid })
      if (!cancelled) setExisting(data)
    })()
    return () => {
      cancelled = true
    }
  }, [userUuid])

  const activeCap = existing?.active_token?.max_amount
    ? existing.active_token.max_amount / 100
    : null
  const chosen = Number(values?.mandate_max_amount || 0)

  return (
    <>
      {/* MUI v4 renders no label from the Select's own `label` prop — the visible label is
          the sibling InputLabel inside an outlined FormControl. Same shape as DeductForm. */}
      <FormControl
        variant="outlined"
        fullWidth
        required
        {...(!isEmpty(errors.mandate_max_amount) ? { error: true } : null)}
      >
        <InputLabel id="emandate-max-amount-label">
          {'Maximum amount per debit'}
        </InputLabel>
        <Select
          name={'mandate_max_amount'}
          labelId="emandate-max-amount-label"
          defaultValue={''}
          displayEmpty
          label={'Maximum amount per debit'}
          validate={validateNotEmpty({ isRequired: true })}
        >
          <MenuItem value="" disabled>
            Select a limit
          </MenuItem>
          {EMANDATE_MAX_AMOUNT_OPTIONS.map((amount) => (
            <MenuItem key={amount} value={amount}>
              {formatRupees(amount)}
            </MenuItem>
          ))}
        </Select>
        <FormHelperText>
          {isEmpty(errors.mandate_max_amount)
            ? 'The most a single auto-debit may collect. Not a charge.'
            : errors.mandate_max_amount}
        </FormHelperText>
      </FormControl>

      <FormControl
        variant="outlined"
        fullWidth
        required
        {...(!isEmpty(errors.emandate_method) ? { error: true } : null)}
      >
        <InputLabel id="emandate-method-label">
          {'Authorisation method'}
        </InputLabel>
        <Select
          name={'emandate_method'}
          labelId="emandate-method-label"
          defaultValue={'upi'}
          label={'Authorisation method'}
          validate={validateNotEmpty({ isRequired: true })}
        >
          {EMANDATE_METHODS.map((m) => (
            <MenuItem key={m.value} value={m.value}>
              {m.label}
            </MenuItem>
          ))}
        </Select>
        <FormHelperText>
          {isEmpty(errors.emandate_method)
            ? 'How the parent authorises it. UPI works for most parents.'
            : errors.emandate_method}
        </FormHelperText>
      </FormControl>

      {activeCap ? (
        <Typography component="div" className={classes.warn}>
          This parent already has a live e-mandate with a limit of{' '}
          <strong>{formatRupees(activeCap)}</strong>. Setting up a new one{' '}
          <strong>replaces it</strong> — the old mandate is revoked once the new
          one is confirmed by the bank.
          {chosen > 0 && chosen < activeCap
            ? ` The new limit you have picked (${formatRupees(
                chosen
              )}) is lower than the current one.`
            : ''}
        </Typography>
      ) : null}

      <Typography component="div" className={classes.note}>
        <strong>Nothing is charged now.</strong> This only authorises future
        auto-debits up to the limit above. The parent gets a WhatsApp with the
        authorisation link, and it is also shown here to copy. Auto-pay goes
        live once their bank confirms the mandate.
      </Typography>
    </>
  )
}

export default SetupEmandateForm
